import { useState } from "react";
import { Download } from "lucide-react";
import {
  getBrandSkusExportV1BrandsSkusExportGetUrl,
  useBrandSkusExportCountV1BrandsSkusExportCountGet,
} from "../api/generated/qcsControlPlane";
import type { BrandSkusExportV1BrandsSkusExportGetParams, Platform } from "../api/generated/model";
import { platformKeys } from "../api/platforms";
import { platformLabel } from "../api/labels";
import { Button, Panel } from "@akaike/ui";
import { Field, Seg } from "./foundation";

type PlatformChoice = "all" | Platform;
type BrandState = "all" | "enabled" | "disabled";
type SkuState = "all" | "enabled" | "disabled";
type Combos = "all" | "only" | "exclude";
type SkuStatus = "active" | "all";

export interface SkuDownloadDefaults {
  /** Prefill from the Brands page's own active/inactive filter. */
  brandState?: BrandState;
  /** Prefill from the page's "include combos" toggle. */
  combos?: Combos;
}

/**
 * Download dialog for the cross-brand SKU export.
 *
 * Four switches decide whether a SKU is scraped and they are independent, so each gets its
 * own control with an explicit caption rather than being folded into one "active" idea.
 * The row count is fetched from the same filter path the download uses, so the number
 * shown is the number of data rows you get -- not an estimate.
 */
export function SkuDownloadModal({
  onClose,
  selectedBrands,
  defaults,
}: {
  onClose: () => void;
  /** Brand names ticked on the page; enables the "only these" scope option. */
  selectedBrands: string[];
  defaults?: SkuDownloadDefaults;
}) {
  const [platform, setPlatform] = useState<PlatformChoice>("all");
  const [combos, setCombos] = useState<Combos>(defaults?.combos ?? "all");
  const [brandState, setBrandState] = useState<BrandState>(defaults?.brandState ?? "all");
  const [skuState, setSkuState] = useState<SkuState>("all");
  const [skuStatus, setSkuStatus] = useState<SkuStatus>("active");
  const [onlySelected, setOnlySelected] = useState(selectedBrands.length > 0);

  const scopeToSelection = onlySelected && selectedBrands.length > 0;
  const params: BrandSkusExportV1BrandsSkusExportGetParams = {
    ...(platform === "all" ? {} : { platform }),
    brand_state: brandState,
    combos,
    sku_state: skuState,
    sku_status: skuStatus,
    ...(scopeToSelection ? { selected: selectedBrands } : {}),
  };

  const countQ = useBrandSkusExportCountV1BrandsSkusExportCountGet(params, {
    query: { placeholderData: (prev) => prev },
  });
  const rows = countQ.data?.rows;
  const empty = rows === 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <Panel style={{ width: "100%", maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <Panel.Head title="Download SKUs">
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </Panel.Head>
        <Panel.Body>
          <div className="col" style={{ gap: 14 }}>
            <Field label="Platform" hint="One platform, or every registered platform in one file.">
              <Seg
                value={platform}
                onChange={setPlatform}
                options={[
                  { value: "all", label: "All" },
                  ...platformKeys().map((p) => ({
                    value: p,
                    label: platformLabel(p),
                  })),
                ]}
              />
            </Field>

            <Field
              label="Combos and multipacks"
              hint="Packs that alias a base product. Uses the effective flag, so a manual bundle override wins over the scraped one."
            >
              <Seg
                value={combos}
                onChange={setCombos}
                options={[
                  { value: "all", label: "Include" },
                  { value: "exclude", label: "Exclude" },
                  { value: "only", label: "Only combos" },
                ]}
              />
            </Field>

            <Field
              label="Brand switch"
              hint="The brand-level toggle on this page. Exported as the brand_enabled column."
            >
              <Seg
                value={brandState}
                onChange={setBrandState}
                options={[
                  { value: "all", label: "All brands" },
                  { value: "enabled", label: "Active only" },
                  { value: "disabled", label: "Inactive only" },
                ]}
              />
            </Field>

            <Field
              label="Per-SKU switch"
              hint="The per-SKU master toggle, independent of the brand switch. A SKU with no override counts as enabled, matching what a run does."
            >
              <Seg
                value={skuState}
                onChange={setSkuState}
                options={[
                  { value: "all", label: "All SKUs" },
                  { value: "enabled", label: "Enabled only" },
                  { value: "disabled", label: "Disabled only" },
                ]}
              />
            </Field>

            <Field
              label="Lifecycle status"
              hint="Active is what a sweep currently sees. All also includes removed and excluded SKUs."
            >
              <Seg
                value={skuStatus}
                onChange={setSkuStatus}
                options={[
                  { value: "active", label: "Active only" },
                  { value: "all", label: "All statuses" },
                ]}
              />
            </Field>

            {selectedBrands.length > 0 && (
              <label className="row" style={{ gap: 8, alignItems: "center", fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={onlySelected}
                  onChange={(e) => setOnlySelected(e.target.checked)}
                />
                <span>
                  Only the {selectedBrands.length} brand
                  {selectedBrands.length === 1 ? "" : "s"} selected on this page
                </span>
              </label>
            )}
          </div>
        </Panel.Body>
        <Panel.Head
          style={{
            borderTop: "1px solid var(--line)",
            borderBottom: 0,
            justifyContent: "flex-end",
          }}
        >
          <span className="faint" style={{ fontSize: 12, marginRight: "auto" }}>
            {countQ.isError
              ? "row count unavailable"
              : rows === undefined
                ? "counting…"
                : `${rows.toLocaleString()} row${rows === 1 ? "" : "s"} across ${countQ.data?.brands.toLocaleString()} brand${countQ.data?.brands === 1 ? "" : "s"}`}
          </span>
          {/* An anchor, not a fetch: the browser streams the response straight to disk, so a
              large export never lands in JS memory. An <a> has no disabled state, so an
              empty selection renders a real disabled Button instead of a dead link. */}
          {empty ? (
            <Button variant="primary" disabled>
              <Download size={14} />
              Download CSV
            </Button>
          ) : (
            <Button asChild variant="primary">
              <a href={getBrandSkusExportV1BrandsSkusExportGetUrl(params)} onClick={onClose}>
                <Download size={14} />
                Download CSV
              </a>
            </Button>
          )}
        </Panel.Head>
      </Panel>
    </div>
  );
}
